<script>
    import { onDestroy, onMount, tick } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getFormatDateAndTime,
        getInsuranceType,
        getText,
        goToURL,
        isObjectEmpty,
        replaceURL,
        showError,
        showException,
        getUrlWithoutSearch,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
        useTableScrollMemory,
        _getName,
    } from "../../Utils/utilFunctions.svelte";

    import {
        cacheInsuranceFunds,
        cacheTrafficAccidentAuthorities,
        cacheWorkersCompensation,
        startRefreshTable,
        stopRefreshTable,
    } from "../../Utils/utilCache.svelte";

    import {
        insuranceFund,
        isMobile,
        isUserStayOnTab,
        tacEncounterIdFullDetails,
        trafficAccidentAuthorities,
        user,
        wcEncounterIdFullDetails,
        workersCompensation,
    } from "../../stores";

    import {
        MultiSearchSelect,
        PageLoading,
        SearchInput,
        SvelteTable,
        ResetFilterLink,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import { InsuranceType } from "../../Utils/utilEnums.svelte";

    import { addIcon, eyeIcon } from "../../Utils/utilConstants.svelte";

    import {
        getEncounterWithWcAndTacDetails,
        getNameOfInsurer,
    } from "../FollowUp/helpers/followUpHelper.svelte";

    import EditNotesModal from "../FollowUp/EditNotesModal.svelte";

    import { syncEncounterLatestNote } from "../PatientEncounter/helpers/encounterHelper.svelte";

    import ViewNotesModal from "../FollowUp/ViewNotesModal.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    import EncounterTableEncounterIdCell from "../PatientEncounter/components/EncounterTableEncounterIdCell.svelte";

    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";
    import SearchSelect from "../../Components/Common/SearchSelect.svelte";

    export let params = {};

    $: isLoading =
        isLoadingEncounter || isLoadingHealthFund || isLoadingAccountDoctors;

    $: if (!$isUserStayOnTab) stopRefreshTable(refreshTableInterval);
    else
        refreshTableInterval = startRefreshTable(
            {
                tableIntervalRef: refreshTableInterval,
            },
            () => reloadPatientEncounter(),
        );

    let isLoadingEncounter = false,
        postEncounterData = [];

    let filters = { badDebtor: Number(0), docType: "1" }, //both preselected
        isLoadingAccountDoctors = false;

    let searchInsuranceTypeArr = [],
        searchPrivateHealthFundArr = [],
        isLoadingHealthFund = false,
        readyToShowMultiFilters = false, // render saved options properly
        healthFundOptionsArr = [],
        isShowPrivateHealthFundSelect = false;

    let searchWorkCoverAuthorityFundArr = [],
        workCoverAuthorityOptionsArr = [],
        isLoadingWorkCoverAuthorityFund = false,
        isShowWorkCoverAuthoritySelect = false;

    let searchTrafficAccidentFundArr = [],
        trafficAccidentOptionsArr = [],
        isLoadingTrafficAccidentAuthority = false,
        isShowTrafficAccidentSelect = false;

    let page = 0,
        pageSize = 25,
        count;

    let isEditNotesModalOpen = false,
        isViewNotesModalOpen = false,
        selectedEncounter = {};

    let selectedDoctors = [];

    let refreshTableInterval, scrollTableContainerRef;

    const { saveScroll, restoreScrollPosition } =
        useTableScrollMemory("UnsentDocuments");

    const sortableHeaderKeys = ["id", "doctor", "scheduledDate", "invoiceDate"];

    const columns = [
        {
            key: "id",
            title: "Enc. ID",
            headerClass:
                "text-left pl-3 sortable-header-decoration rounded-tl-xl",
            class: "px-3 text-left",
            renderComponent: {
                component: EncounterTableEncounterIdCell,
                props: {
                    clickable: true,
                },
            },
            csvContent: (v) => getText(v.id),
        },
        {
            key: "patientName",
            title: "Name",
            headerClass: "text-left pl-3",
            class: "px-3 text-left",
            renderValue: (v) => {
                 return _getName(v.patient)
            },
        },
        {
            key: "invoiceNumber",
            title: "Document No.",
            headerClass: "text-left pl-3",
            class: "px-3 text-left",
            renderValue: (v) => _getUnsentDocumentId(
                v.estimates.filter((r) => r.status == 1), 
                v.invoices.filter((r) => r.status == 1)
            ),
            csvContent: (v) => _getUnsentDocumentId(
                v.estimates.filter((r) => r.status == 1), 
                v.invoices.filter((r) => r.status == 1), 
                true),
        },
        {
            key: "invoiceDate",
            title: "Created Date",
            headerClass: "text-left pl-3 sortable-header-decoration",
            class: "px-3 text-left",
            renderValue: (v) => _getUnsentDocumentCreatedDate(
                v.estimates.filter((r) => r.status == 1), 
                v.invoices.filter((r) => r.status == 1)
            ),
            csvContent: (v) => _getUnsentDocumentCreatedDate(
                v.estimates.filter((r) => r.status == 1), 
                v.invoices.filter((r) => r.status == 1),
                true
            )
        },
        {
            key: "doctor",
            title: "Doctor",
            headerClass: "text-left pl-3 sortable-header-decoration",
            class: "px-3 text-left",
            renderValue: (v) => _getDoctorName(v.doctor),
            csvContent: (v) => _getDoctorName(v.doctor, true),
        },
        {
            key: "scheduledDate",
            title: "Scheduled Date",
            headerClass: "text-left pl-3 sortable-header-decoration",
            class: "px-3 text-left",
            renderValue: (v) => _getScheduleDate(v.scheduledDate),
        },
        {
            key: "insuranceType",
            title: "Insurance Type",
            headerClass: "text-left pl-3",
            class: "px-3 text-left",
            renderValue: (v) => _getInsuranceType(v.patient),
        },
       /*  {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl",
            class: "px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(v.hasNotes),
            csvExportHidden: true,
        }, */
    ];

    onMount(async () => {
        getUrlWithoutSearch();

        await updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        await loadPatientEncounters(
            page * pageSize,
            pageSize,
            _getSearchParams(),
        );

        readyToShowMultiFilters = true;
    });

    onDestroy(() => {
        stopRefreshTable(refreshTableInterval);

        $wcEncounterIdFullDetails = [];
        $tacEncounterIdFullDetails = [];
    });

    const updateSearchParamsByQuery = async (query) => {
        if (query) {
            const arr = decodeSearchParams(query).split("&");

            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];
                
                if (arr[i].includes("docType="))
                    filters.docType = arr[i].split("=")[1];

                if (arr[i].includes("encounterId="))
                    filters.encounterId = arr[i].split("=")[1].toString();

                if (arr[i].includes("doctorId=")) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }

                if (arr[i].includes("patientInsuranceType=")) {
                    searchInsuranceTypeArr = arr[i]
                        .split("=")[1]
                        .split(",")
                        .map(Number);

                    handleInsuranceTypeChange(searchInsuranceTypeArr, {
                        requireReloadFunction: false,
                    });
                }

                if (arr[i].includes("patientInsuranceID="))
                    searchPrivateHealthFundArr = arr[i]
                        .split("=")[1]
                        .split(",");

                if (arr[i].includes("workCoverAuthorityID="))
                    searchWorkCoverAuthorityFundArr = arr[i]
                        .split("=")[1]
                        .split(",");

                if (arr[i].includes("trafficAccidentAuthorityID="))
                    searchTrafficAccidentFundArr = arr[i]
                        .split("=")[1]
                        .split(",");

                if (arr[i].includes("fromDate="))
                    filters.dateFrom = arr[i].split("=")[1];

                if (arr[i].includes("toDate="))
                    filters.dateTo = arr[i].split("=")[1];

                if (arr[i].includes("commsDisabled=1"))
                    filters.commsDisabled = true;

                if (arr[i].includes("badDebtor="))
                    filters.badDebtor = Number(arr[i].split("=")[1]);
                else filters.badDebtor = Number(-1);
            }

            updateURL();
        }
    };

    const loadPatientEncounters = async (start, limit, params) => {
        isLoadingEncounter = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await apiMethods.getMethod
            .getPatientEncounter(start, limit, params)
            .then(async (res) => await _handlePatientEncounters(res))
            .then(() => _updateHeaderSortIcon())
            .catch((err) => showException(err));

        isLoadingEncounter = false;
    };

    const reloadPatientEncounter = async () => {
        saveTableScroll();
        await apiMethods.getMethod
            .getPatientEncounter(page * pageSize, pageSize, _getSearchParams())
            .then(
                async (res) => await _handlePatientEncounters(res, false, true),
            )
            .then(() => _updateHeaderSortIcon())
            .then(() => restoreTableScroll())
            .catch((err) => showException(err));
    };

    const saveTableScroll = () => {
        if (scrollTableContainerRef)
            saveScroll(
                scrollTableContainerRef.scrollTop,
                scrollTableContainerRef.scrollLeft,
            );
    };

    const restoreTableScroll = () => {
        if (!scrollTableContainerRef) return;

        scrollTableContainerRef.scrollTop = restoreScrollPosition().top;
        scrollTableContainerRef.scrollLeft = restoreScrollPosition().left;

        saveScroll();
    };

    const loadInsuranceFund = async () => {
        isLoadingHealthFund = true;

        await cacheInsuranceFunds();

        _updateInsuranceFundsOptions();

        isLoadingHealthFund = false;
    };

    const loadWorkCoverAuthorityFund = async () => {
        isLoadingWorkCoverAuthorityFund = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheWorkersCompensation();

        _updateWorkCoverOptions();

        isLoadingWorkCoverAuthorityFund = false;
    };

    const loadTrafficAccidentFund = async () => {
        isLoadingTrafficAccidentAuthority = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await cacheTrafficAccidentAuthorities();

        _updateTrafficAccidentOptions();

        isLoadingTrafficAccidentAuthority = false;
    };

    const updateURL = () => {
        replaceURL(
            `/UnsentDocuments/page=${page}&size=${pageSize}&${_getParamsString()}`,
        );
    };

    const onPageSizeChange = (newSize) => {
        pageSize = newSize;
        page = 0;

        updateURL();

        loadPatientEncounters(0, pageSize, _getSearchParams());
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadPatientEncounters(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (
            key == "id" ||
            key == "invoiceNumber" ||
            key == "doctor" ||
            key == "insuranceDetails"
        ) {
            const classArr = className.split(" ");

            if (className.includes("invoice")) {
                const targetClass = classArr.find((cls) =>
                    cls.includes("invoice"),
                );
                const invoiceId = targetClass.replace("invoice", "");
                goToURL(`/Invoices/view/${invoiceId}`);
            }
            if (className.includes("estimate")) {
                const targetClass = classArr.find((cls) =>
                    cls.includes("estimate"),
                );
                const estimateId = targetClass.replace("estimate", "");
                goToURL(`/Estimates/view/${estimateId}`);
            }
            if (className.includes("doctor")) {
                const targetClass = classArr.find((cls) =>
                    cls.includes("doctor"),
                );
                const doctorId = targetClass.replace("doctor", "");
                goToURL(`/Account/view/${doctorId}`);
            }
        } else if (key == "buttons") {
            if (className.includes("notesIcon")) _openEditNotesModal(rowData);
            else if (className.includes("viewIcon"))
                _openViewNotesModal(rowData);
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadPatientEncounters(0, pageSize, _getSearchParams());
        }
    };

    const handleFilterSelect = () => {
        page = 0;
        updateURL();
        loadPatientEncounters(0, pageSize, _getSearchParams());
    };

    const handleInsuranceTypeChange = async (
        searchResult,
        { requireReloadFunction = true },
    ) => {
        searchInsuranceTypeArr = [...searchResult];

        if (!searchInsuranceTypeArr?.length) {
            isShowPrivateHealthFundSelect = false;
            searchPrivateHealthFundArr = [];

            isShowWorkCoverAuthoritySelect = false;
            searchWorkCoverAuthorityFundArr = [];

            isShowTrafficAccidentSelect = false;
            searchTrafficAccidentFundArr = [];
        } else {
            if (
                searchInsuranceTypeArr.length &&
                searchInsuranceTypeArr.includes(0)
            )
                isShowPrivateHealthFundSelect = true;
            else {
                isShowPrivateHealthFundSelect = false;
                searchPrivateHealthFundArr = [];
            }

            if (searchInsuranceTypeArr.includes(Number(16))) {
                isShowWorkCoverAuthoritySelect = true;

                await loadWorkCoverAuthorityFund();
            } else {
                isShowWorkCoverAuthoritySelect = false;
                searchWorkCoverAuthorityFundArr = [];
            }

            if (searchInsuranceTypeArr.includes(Number(17))) {
                isShowTrafficAccidentSelect = true;

                await loadTrafficAccidentFund();
            } else {
                isShowTrafficAccidentSelect = false;
                searchTrafficAccidentFundArr = [];
            }
        }

        requireReloadFunction && handleFilterSelect();
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const handleHealthFundTypeChange = (searchResult) => {
        searchPrivateHealthFundArr = [...searchResult];

        handleFilterSelect();
    };

    const handleWorkCoverAuthorityTypeChange = (searchResult = []) => {
        searchWorkCoverAuthorityFundArr = [...searchResult];

        handleFilterSelect();
    };

    const handleTrafficAccidentAuthorityTypeChange = (searchResult = []) => {
        searchTrafficAccidentFundArr = [...searchResult];

        handleFilterSelect();
    };

    const _handlePatientEncounters = async (
        res,
        isCsvContent = false,
        isReload = false,
    ) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load encounters, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            let result = isReload ? postEncounterData : [];

            if (!isCsvContent) {
                postEncounterData = [...res.results];
                count = res.total;
            }

            await loadInsuranceFund();

            await cacheWorkersCompensation();

            await cacheTrafficAccidentAuthorities();

            await tick();

            postEncounterData =
                await getEncounterWithWcAndTacDetails(postEncounterData);

            result = await _mapNameOfInsurer(postEncounterData);

            result = await syncEncounterLatestNote(result);

            if (isCsvContent) return result;
            else postEncounterData = [...result];
        }
    };

    const _mapNameOfInsurer = (arr = []) =>
        arr.map((encounter) => ({
            ...encounter,
            nameOfInsurer: getNameOfInsurer(encounter),
        }));

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    const { sort } = params;
                    const title = row.childNodes[0].data;

                    if (title == "Encounter ID") {
                        if (sort == "id") row.appendChild(upSpan);
                        else if (sort == "idDesc") row.appendChild(downSpan);
                    } else if (title == "Doctor") {
                        if (sort == "doctor") row.appendChild(upSpan);
                        else if (sort == "doctorDesc")
                            row.appendChild(downSpan);
                    } else if (title == "Scheduled Date") {
                        if (sort == "scheduledDate") row.appendChild(upSpan);
                        else if (sort == "scheduledDateDesc")
                            row.appendChild(downSpan);
                    } else if (title == "Invoice Due Date") {
                        if (sort == "invoiceDate") row.appendChild(upSpan);
                        else if (sort == "invoiceDateDesc")
                            row.appendChild(downSpan);
                    }
                });
            }, 0);
        }
    };

    const _getSearchParams = () => {
        let params = {
            "filter[unsentdocuments]": "1", //both by default
            "filter[billingStatus]": "1,2,3,4",
            "filter[excludedemoaccounts]": 1,
        };

        if (filters.sort) params.sort = filters.sort;

        if (filters.encounterId) params["filter[id]"] = filters.encounterId;

        if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

        if(filters.docType) params["filter[unsentdocuments]"] = filters.docType;

        if (searchInsuranceTypeArr.length)
            params["filter[patientInsuranceType]"] =
                searchInsuranceTypeArr.join(",");

        if (searchPrivateHealthFundArr.length)
            params["filter[patientInsuranceID]"] =
                searchPrivateHealthFundArr.join(",");

        if (searchWorkCoverAuthorityFundArr.length)
            params["filter[patientWorkCoverAuthority]"] =
                searchWorkCoverAuthorityFundArr.join(",");

        if (searchTrafficAccidentFundArr.length)
            params["filter[patientTrafficAccidentAuthority]"] =
                searchTrafficAccidentFundArr.join(",");

        if (filters.dateFrom)
            params["filter[scheduledDateFrom]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[scheduledDateTo]"] = filters.dateTo;

        if (filters.commsDisabled) params["filter[disableNotifications]"] = 1;

        if (!isNaN(filters.badDebtor) && Number(filters.badDebtor) >= 0)
            params["filter[badDebtor]"] = filters.badDebtor;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;
            if (params["filter[id]"])
                str += `&encounterId=${params["filter[id]"]}`;
            if (params["filter[doctorId]"])
                str += `&doctorId=${params["filter[doctorId]"]}`;
            if (params["filter[patientInsuranceType]"])
                str += `&patientInsuranceType=${params["filter[patientInsuranceType]"]}`;
            if (params["filter[patientInsuranceID]"])
                str += `&patientInsuranceID=${params["filter[patientInsuranceID]"]}`;
            if (params["filter[patientWorkCoverAuthority]"])
                str += `&workCoverAuthorityID=${params["filter[patientWorkCoverAuthority]"]}`;
            if (params["filter[patientTrafficAccidentAuthority]"])
                str += `&trafficAccidentAuthorityID=${params["filter[patientTrafficAccidentAuthority]"]}`;
            if (params["filter[scheduledDateFrom]"])
                str += `&fromDate=${params["filter[scheduledDateFrom]"]}`;
            if (params["filter[scheduledDateTo]"])
                str += `&toDate=${params["filter[scheduledDateTo]"]}`;
            if (params["filter[unsentdocuments]"])
                str += `&docType=${params["filter[unsentdocuments]"]}`;
            if (params["filter[disableNotifications]"])
                str += `&commsDisabled=1`;
            if (
                !isNaN(params["filter[badDebtor]"]) &&
                Number(params["filter[badDebtor]"]) >= 0
            )
                str += `&badDebtor=${params["filter[badDebtor]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _getUnsentDocumentId = (estimates = [], invoices = [], isCsvContent = false) => {
        if (!invoices.length && !estimates.length) return "-";

        const result = [];
        let component;

        estimates.map((estimate) => {
            component = isCsvContent
                ? `${estimate.estimateNumber}`
                : `<div class="text-secondary cursor-pointer hover:underline estimate${estimate.id}">${estimate.estimateNumber}</div>`;

            result.push(component);
        });

        invoices.map((invoice) => {
            component = isCsvContent
                ? `${invoice.invoiceNumber}`
                : `<div class="text-secondary cursor-pointer hover:underline invoice${invoice.id}">${invoice.invoiceNumber}</div>`;

            result.push(component);
        });

        return result
            .toString()
            .replaceAll(",", isCsvContent ? "\r\n" : "<br>");
    };

    const _getUnsentDocumentCreatedDate = (estimates = [], invoices = [], isCsvContent = false) => {
        if (!invoices.length && !estimates.length) return "-";

        const result = [];
        let component;

        estimates.map((estimate) => {
            component = isCsvContent
                ? `${getFormatDateAndTime(estimate.createdDate)}`
                : `<div class=" estimate${estimate.id}">${getFormatDateAndTime(estimate.createdDate, "/", true, false, " ")}</div>`;

            result.push(component);
        });

        invoices.map((invoice) => {
            component = isCsvContent
                ? `${getFormatDateAndTime(invoice.createdDate)}`
                : `<div class=" invoice${invoice.id}">${getFormatDateAndTime(invoice.createdDate, "/", true, false, " ")}</div>`;

            result.push(component);
        });

        return result
            .toString()
            .replaceAll(",", isCsvContent ? "\r\n" : "<br>");
    };

    const _getDoctorName = (doctor = {}, isCsvContent = false) => {
        if (isObjectEmpty(doctor)) return "-";

        const { fullName, id } = doctor;

        return isCsvContent
            ? `${fullName}`
            : `<div class="text-secondary cursor-pointer hover:underline doctor${id}">${fullName}</div>`;
    };

    const _getScheduleDate = (date) => {
        if (!date) return "-";

        const dateArr = date.split("T")[0].split("-");

        if (Number(dateArr[0]) < 1900) return "-";
        return getFormatDateAndTime(date, "/", true, false, " ");
    };

    const _getInsuranceType = (patient = {}) => {
        if (isObjectEmpty(patient)) return "-";

        const { insuranceType } = patient;

        return getInsuranceType(insuranceType);
    };

    const _renderActionIcons = (hasNotes = false) => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="viewIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white ${hasNotes ? "" : "hidden"}" data-tip="View all notes">
                        <i class="viewIcon ${eyeIcon}"></i>
                    </div>
                    <div class="notesIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Add note">
                        <i class="notesIcon ${addIcon}"></i>
                    </div>
                </div>`;
    };

    const _updateInsuranceFundsOptions = () => {
        healthFundOptionsArr = $insuranceFund.map((fund) => ({
            key: fund.id,
            value: fund.name,
        }));
    };

    const _updateWorkCoverOptions = () => {
        workCoverAuthorityOptionsArr = $workersCompensation.map((wc) => ({
            key: wc.id,
            value: wc.name,
        }));
    };

    const _updateTrafficAccidentOptions = () => {
        trafficAccidentOptionsArr = $trafficAccidentAuthorities.map((tac) => ({
            key: tac.id,
            value: tac.name,
        }));
    };

    const _openEditNotesModal = (encounter = {}) => {
        selectedEncounter = { ...encounter };
        isEditNotesModalOpen = true;
    };

    const _closeEditNotesModal = () => {
        selectedEncounter = {};
        isEditNotesModalOpen = false;
    };

    const _openViewNotesModal = (encounter = {}) => {
        selectedEncounter = { ...encounter };
        isViewNotesModalOpen = true;
    };

    const _closeViewNotesModal = (hasSelectedNotesChanged = false) => {
        selectedEncounter = {};
        isViewNotesModalOpen = false;

        hasSelectedNotesChanged &&
            loadPatientEncounters(
                page * pageSize,
                pageSize,
                _getSearchParams(),
            );
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div
        class="flex flex-0 items-start {$isMobile
            ? 'flex-col-reverse gap-y-4'
            : 'justify-between'}"
    >
        <div class="flex items-end justify-start flex-wrap gap-4">
            <SearchInput
                bind:value={filters.encounterId}
                title="Encounter ID"
                type="number"
                inputFunc={() => {
                    if (!isNaN(filters.encounterId)) handleFilterSelect();
                }}
                id="encounterIdInput"
            />

            <DoctorMultiSearchSelect
                bind:searchResult={selectedDoctors}
                onChangeFunc={handleSelectDoctor}
                inputClass="w-32"
                zIndex="3"
            />

            {#if readyToShowMultiFilters}
                <MultiSearchSelect
                    title="Insurance Type"
                    inputID="patientInsuranceType"
                    isDisabled={isLoadingEncounter}
                    optionsArr={InsuranceType}
                    bind:searchResult={searchInsuranceTypeArr}
                    onChangeFunc={handleInsuranceTypeChange}
                    inputClass="w-32"
                />

                {#if isShowPrivateHealthFundSelect}
                    <MultiSearchSelect
                        title="Private Health Fund"
                        inputID="patientHealthFundType"
                        isDisabled={isLoadingEncounter}
                        optionsArr={healthFundOptionsArr}
                        bind:searchResult={searchPrivateHealthFundArr}
                        onChangeFunc={handleHealthFundTypeChange}
                        inputClass="w-32"
                    />
                {/if}

                {#if isShowWorkCoverAuthoritySelect}
                    <MultiSearchSelect
                        title="Work Cover Authority"
                        inputID="consentDueWorkCoverAuthority"
                        isDisabled={isLoadingWorkCoverAuthorityFund}
                        optionsArr={workCoverAuthorityOptionsArr}
                        bind:searchResult={searchWorkCoverAuthorityFundArr}
                        onChangeFunc={handleWorkCoverAuthorityTypeChange}
                        inputClass="w-32"
                    />
                {/if}

                {#if isShowTrafficAccidentSelect}
                    <MultiSearchSelect
                        title="Traffic Accident Authority"
                        inputID="consentDueTrafficAccidentAuthority"
                        isDisabled={isLoadingTrafficAccidentAuthority}
                        optionsArr={trafficAccidentOptionsArr}
                        bind:searchResult={searchTrafficAccidentFundArr}
                        onChangeFunc={handleTrafficAccidentAuthorityTypeChange}
                        inputClass="w-32"
                    />
                {/if}
            {/if}

            <SearchSelect
                bind:value={filters.docType}
                title="Document Type"
                onChangeFunc={handleFilterSelect}
                isDisabled={isLoadingEncounter}
            >
                <option value="1">All</option>
                <option value="2">Quotes</option>
                <option value="3">Invoices</option>
            </SearchSelect>

            {#if !$user.settings?.disableDateRangeInput}
                <SearchDateRangeInput
                    bind:startDate={filters.dateFrom}
                    bind:endDate={filters.dateTo}
                    title="Date Range"
                    inputClass="borderB w-[272px]"
                    on:change={handleFilterSelect}
                    showClearButton
                    disable={isLoadingEncounter}
                    showScrollYearOptions
                />
            {:else}
                <SearchInput
                    bind:value={filters.dateFrom}
                    title="Date from"
                    type="date"
                    inputClass="borderB"
                    inputFunc={handleFilterSelect}
                    isDisable={isLoadingEncounter}
                    maxDate={filters.dateTo ?? undefined}
                    showScrollYearOptions
                />

                <SearchInput
                    bind:value={filters.dateTo}
                    title="Date to"
                    type="date"
                    inputClass="borderB"
                    inputFunc={handleFilterSelect}
                    isDisable={isLoadingEncounter}
                    minDate={filters.dateFrom ?? undefined}
                    showScrollYearOptions
                />
            {/if}

            <ResetFilterLink
                bind:filter={filters}
                specialVariables={{
                    searchInsuranceTypeArr,
                    searchPrivateHealthFundArr,
                }}
                onResetFilter={() => {
                    searchInsuranceTypeArr = [];
                    handleInsuranceTypeChange(searchInsuranceTypeArr, {
                        requireReloadFunction: false,
                    });
                    searchPrivateHealthFundArr = [];

                    clearFilterDoctor();
                }}
            />
        </div>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isLoading}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
                bind:this={scrollTableContainerRef}
            >
                <SvelteTable
                    {columns}
                    rows={postEncounterData}
                    customRow="align-middle"
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={postEncounterData}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: postEncounterData,
            fileName: "Patient Payment Due",
            allDataApi: async () => {
                let res = await apiMethods.getMethod.getPatientEncounter(
                    0,
                    999999,
                    _getSearchParams(),
                );

                const results = await _handlePatientEncounters(res, true);

                return { results };
            },
        }}
    />
</div>

{#if isEditNotesModalOpen}
    <EditNotesModal
        isModalOpen={isEditNotesModalOpen}
        encounter={selectedEncounter}
        confirmAction={() =>
            loadPatientEncounters(
                page * pageSize,
                pageSize,
                _getSearchParams(),
            )}
        closeModal={_closeEditNotesModal}
    />
{/if}

{#if isViewNotesModalOpen}
    <ViewNotesModal
        isModalOpen={isViewNotesModalOpen}
        encounter={selectedEncounter}
        closeModal={_closeViewNotesModal}
    />
{/if}
